import { StringUtils } from "abweb/utils/StringUtils";

/**
 * Collection of static helper methods to support external clipboard usage.
 * Most of the methods are related to supporting copy/paste to/from Excel.
 */
export class ClipboardUtils {
  /**
   * Converts a rectangular array of string cells into a string compatible for paste into Excel.
   * @param cells Rectangular array of cells.
   * @returns {string} string for Excel
   */
  static encodeCellsForExcel(cells) {
    const rows = cells;
    const rowItems = [];
    for (const row of rows) {
      for (let n = 0; n < row.length; n++) {
        row[n] = ClipboardUtils.encodeCellForExcel(row[n]);
      }
      const rowItem = row.join("\t");
      rowItems.push(rowItem);
    }
    return rowItems.join("\r\n");
  }

  /**
   * Converts a string (presumably as a copy from Excel) into a rectangular array of string cells.
   * @param textData Source string presumably in Excel encoded format
   * @returns rectangular array of cells
   */
  static decodeCellsFromExcel(textData) {
    const CRLF = "\r\n";
    const LF = "\n";
    const TAB = "\t";

    // determine what delimiter to use. Chrome uses CRLF, while Firefox uses just LF. If we find
    // LF without CRLF, we will use just LF; otherwise, CRLF
    let delimiter = CRLF;
    if (StringUtils.contains(textData, LF) && !StringUtils.contains(textData, CRLF)) {
      delimiter = LF;
    }
    const copyBuffer = [];

    // Excel seems to always append CRLF when you copy cell(s)
    if (StringUtils.endsWith(textData, delimiter)) {
      // Strip trailing delimiter
      textData = textData.substr(0, textData.length - delimiter.length);
    }

    // Per MH-29904, we need to look for "special cells" which have been escaped by Excel.
    // A "special cell" is one which has an embedded TAB or \n in it. These cells will be wrapped with a leading and trailing quote, and will escape any internal quotes with 2 quotes.
    // The sanitizedTextData should contain only TABs that are true cell delimiters, and only delimiters at "row" boundaries
    // Any embedded TABs and \ns will be escaped using tabToken and newLineToken and will be fixed up below
    const tabToken = "__AbInitioTab__";
    const newlineToken = "__AbInitioNewline__";
    let sanitizedTextData = ClipboardUtils.parseSpecialExcelCells(textData, tabToken, newlineToken, delimiter);
    const textRows = sanitizedTextData.split(delimiter);

    // At this point, textRows should consist of an Array of real lines

    // Now parse cells in each line
    let maxColCount = 0;
    for (const textRow of textRows) {
      const row = [];
      const textCells = textRow.split(TAB);
      for (let textCell of textCells) {
        row.push(textCell);
      }
      if (row.length > maxColCount) {
        maxColCount = row.length;
      }
      copyBuffer.push(row);
    }

    // In case of an irregular cell matrix,
    // ensure that all rows have the same number of cells
    for (const row of copyBuffer) {
      while (row.length < maxColCount) {
        row.push("");
      }
    }

    // Now replace any of our special tokens
    for (let rowIndex = 0; rowIndex < copyBuffer.length; rowIndex++) {
      let row = copyBuffer[rowIndex];
      for (let cellIndex = 0; cellIndex < row.length; cellIndex++) {
        row[cellIndex] = row[cellIndex].replaceAll(tabToken, "\t").replaceAll(newlineToken, "\r\n");
      }
    }
    return copyBuffer;
  }

  /**
   * This method searches the textData (raw clipboard text) looking for "special cells".
   * If we find them, we fix them by replacing embedded TAB and \n characters with the 2 tokens passed as args.
   * In addition, we denormalize any embedded quotes that Excel may have added in these special cells.
   * @param textData Raw clipboard string
   * @param tabToken Special token used to replace any embedded TABs in a special cell
   * @param newlineToken Special token used to replace any embedded \ns in a special cell
   * @param delimiter Delimiter used by Excel to denote row boundaries (\r\n on Chrome, and \n on Firefox)
   * @private
   */
  static parseSpecialExcelCells(textData, tabToken, newlineToken, delimiter) {
    const NEWLINE = "\n"; // Seems that any embedded newline is encoded with a \n
    const TAB = "\t";
    const QUOTE = '"';
    let output = "";
    let cellStartIndex = 0;
    while (cellStartIndex < textData.length) {
      if (textData.charAt(cellStartIndex) === QUOTE) {
        // We MAY have a special cell because it starts with a QUOTE
        // So look for one of our possible embedded TAB or NEWLINE
        let tokenIndex = StringUtils.indexOfAny(textData, [TAB, NEWLINE], cellStartIndex);
        if (tokenIndex < 0) {
          // No tokens found in remainder of buffer so this is just a regular cell
          if (moveToNextCellBoundary(cellStartIndex + 1)) {
            continue;
          } else {
            break;
          }
        }

        // We have a token at tokenIndex
        // So now look for a QUOTE followed by a cell boundary (TAB, delimiter, or EOD)
        let nextSpecialCellBoundaryTuple = getCellBoundaryTuple(tokenIndex + 1, true);
        if (nextSpecialCellBoundaryTuple == null) {
          // No match so we do not have a special cell
          // No tokens found in remainder of buffer so this is just a regular cell
          if (moveToNextCellBoundary(tokenIndex + 1)) {
            continue;
          } else {
            break;
          }
        }

        // Verify this is actually a special cell by ensuring the token is embedded within quotes
        // and not a delimiter between separate quoted cells
        const potentialCellContent = textData.substring(cellStartIndex + 1, nextSpecialCellBoundaryTuple.index);
        const tokenRelativePos = tokenIndex - (cellStartIndex + 1);
        if (tokenRelativePos >= 0 && tokenRelativePos < potentialCellContent.length) {
          // Check if there's an unescaped quote before our token position
          let hasUnescapedQuoteBefore = false;
          for (let i = 0; i < tokenRelativePos; i++) {
            if (potentialCellContent.charAt(i) === QUOTE) {
              // Check if this quote is escaped (followed by another quote)
              if (i + 1 < potentialCellContent.length && potentialCellContent.charAt(i + 1) === QUOTE) {
                i++; // Skip the next quote as it's part of the escape sequence
              } else {
                // Found an unescaped quote before the token - this means we have separate cells
                hasUnescapedQuoteBefore = true;
                break;
              }
            }
          }
          if (hasUnescapedQuoteBefore) {
            // This is not a special cell, just separate quoted cells
            if (moveToNextCellBoundary(cellStartIndex + 1)) {
              continue;
            } else {
              break;
            }
          }
        }

        // So we now know we have a special cell so process it
        // This will strip the leading and trailing QUOTEs
        let newCell = textData.substring(cellStartIndex + 1, nextSpecialCellBoundaryTuple.index);

        // One more test of a special case. If newCell contains any unmatched quotes, then we reject this as a special cell
        if (!validateSpecialCell(newCell)) {
          // We have been rejected as a special cell
          // So just look for next cell boundary
          if (moveToNextCellBoundary(cellStartIndex + 1)) {
            continue;
          } else {
            break;
          }
        }

        // And tokenize each embedded special char
        newCell = newCell.replace(/\n/g, newlineToken).replace(/\t/g, tabToken);

        // And replace consecutive QUOTEs with a single quote
        newCell = newCell.replace(/""/g, '"');
        output += newCell;

        // Add the delimiter we found
        if (nextSpecialCellBoundaryTuple.boundary.length > 1) {
          output += nextSpecialCellBoundaryTuple.boundary.substring(1);
        }
        cellStartIndex = nextSpecialCellBoundaryTuple.index + nextSpecialCellBoundaryTuple.boundary.length;
      } else {
        // cellStartIndex cannot be the start of a special cell,
        // so find next cell boundary
        if (!moveToNextCellBoundary(cellStartIndex)) {
          break;
        }
      }
    }
    return output;

    // Uses textData and delimiter
    function getCellBoundaryTuple(startIndex, includeQuote) {
      let searchString = textData.substring(startIndex);
      let boundaryTab = includeQuote ? QUOTE + TAB : TAB;
      let indexTab = searchString.indexOf(boundaryTab);
      if (indexTab < 0) {
        indexTab = Number.MAX_SAFE_INTEGER;
      }
      let boundaryDelim = includeQuote ? QUOTE + delimiter : delimiter;
      let indexDelim = searchString.indexOf(boundaryDelim);
      if (indexDelim < 0) {
        indexDelim = Number.MAX_SAFE_INTEGER;
      }
      let indexTrailingQuote = Number.MAX_SAFE_INTEGER;
      if (includeQuote && searchString.charAt(searchString.length - 1) === QUOTE) {
        indexTrailingQuote = searchString.length - 1;
      }

      // Return min of these numbers
      let index = Math.min(indexTab, indexDelim, indexTrailingQuote);
      if (index === Number.MAX_SAFE_INTEGER) {
        // Did not find any
        return null;
      }
      if (index === indexTab) {
        return {
          index: startIndex + indexTab,
          boundary: boundaryTab
        };
      } else if (index === indexDelim) {
        return {
          index: startIndex + indexDelim,
          boundary: boundaryDelim
        };
      } else if (index === indexTrailingQuote) {
        return {
          index: textData.length - 1,
          boundary: QUOTE
        };
      }
      return null;
    }

    // Moves to next cell boundary and copies substring to output
    // Updates output and cellStartIndex
    function moveToNextCellBoundary(startIndex) {
      let nextCellBoundaryTuple = getCellBoundaryTuple(startIndex, false);
      if (nextCellBoundaryTuple == null) {
        // No more cell boundaries so we are done
        output += textData.substring(cellStartIndex);
        return false;
      }
      output += textData.substring(cellStartIndex, nextCellBoundaryTuple.index + nextCellBoundaryTuple.boundary.length);
      cellStartIndex = nextCellBoundaryTuple.index + nextCellBoundaryTuple.boundary.length;
      return true;
    }

    // This function tests a candidate special cell (with leading and trailing quotes already removed) to see if it should be rejected because:
    // 1) It contains mismatched quotes
    // 2) It contains a \r\n pair which means it came from Chrome and it is not an embedded newline.
    // Returns true if the cell is OK, or false if we should reject this as a special cell
    function validateSpecialCell(candidateString) {
      if (candidateString.indexOf("\r\n") >= 0) {
        // Reject based on \r\n rule
        return false;
      }
      let quoteCount = StringUtils.getTokenCount(candidateString, QUOTE);
      // If we have an even number of QUOTEs, we are OK
      return quoteCount % 2 === 0;
    }
  }

  /**
   * Called from encodeCellsForExcel() to process each cell to make it suitable for Excel.
   * If the cell has any embedded \n chars, we need to wrap it in quotes. If so, we have to
   * encode the quote by preceding it with another quote.
   * @param cell
   */
  static encodeCellForExcel(cell) {
    if (cell == null) {
      return "";
    }
    if (typeof cell === "number") {
      return cell.toString();
    }
    if (cell.indexOf("\t") >= 0) {
      // Replace TABs with SPACE since Excel seems to eat them anyway
      const EMBEDDED_TAB_TOKEN = " ";
      cell = cell.replaceAll("\t", EMBEDDED_TAB_TOKEN);
    }
    if (cell.indexOf("\n") < 0) {
      // No need to do anything
      return cell;
    }

    // Need to wrap in quotes and encode any other quotes
    const escapedCell = cell.replaceAll('"', '""');
    return `"${escapedCell}"`;
  }

  /**
   * This function is trying to fix a string that comes from Excel
   * When copying a string from Excel to the clipboard ,
   * if the string contains \n , Excel will do 2 things :
   * 1. wrap the string with quotes
   * 2. escape any quote with another quote
   * For example :
   * "foo"           -> "foo" (no change)
   * foo\ngoo        -> "foo\ngoo"
   * foo\n"goo"      -> "foo\n""goo"""
   *
   * this function will get the right side text and return the left side
   * reverse the Excel clipboard fixup
   */
  static stripEmbeddedQuotesExcelFixup(s) {
    const LF = "\n";
    const QUOTE = '"';
    if (s.indexOf(QUOTE) < 0) {
      // No contained quotes
      return s;
    }

    // Per MH-12493 it seems that Excel uses only newline delimiters inside of cells, so it should be sufficient to search for only newlines
    // which would also work for CRLF as well
    if (s.indexOf(LF) < 0) {
      return s;
    }
    if (StringUtils.startsWith(s, QUOTE) && StringUtils.endsWith(s, QUOTE)) {
      // We have leading and trailing quotes, so strip them
      s = s.substring(1, s.length - 1);

      // and replace any escaped quotes within
      if (s.indexOf('""') >= 0) {
        // We have some escaped quotes
        s = s.replaceAll('""', QUOTE);
      }
    }
    return s;
  }

  /**
   * It's possible to have a string containing regular \n newlines. Excel uses \r\n newlines, so replace with
   * those in order to be more compatible (and make the encode/decode from excel functions behave properly).
   * @param {string} s
   * @returns {string}
   */
  static replaceNewlinesWithExcelNewlines(s) {
    // https://www.regextester.com/
    // some more comments in EI-17017
    // ?<! is a negative lookbehind, which means, we want to match all the \n that do NOT precede by \r
    //return s.replace(new RegExp(`(?<!\r)\n`, "g"), "\r\n");

    // FF 78.0 was ok with the negative lookbehind, but FF 68 wasn't. This caused a failure (MH-13068)
    // So, I replaced the fancy regex with a simple match + a function to decide what to do :
    return s.replace(/\n/g, (match, offset, input) => {
      // if offset is zero, '\n' is the first char, replace it with '\r\n'
      if (offset === 0) {
        return `\r\n`;
      }
      // if the char before '\n' is NOT '\r' , replace with '\r\n'
      else if (input.charAt(offset - 1) !== `\r`) {
        return `\r\n`;
      }
      // else = the match is \r\n, no need to replace, keep the '\n'
      else {
        return `\n`;
      }
    });
  }
  static simpleCopyToSystemClipboard(s) {
    let textArea = document.createElement("textarea");
    textArea.value = s;
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
      document.execCommand("copy");
    } catch (err) {
      console.error("Oops, unable to copy", err);
    }
    document.body.removeChild(textArea);
  }
}
//# sourceMappingURL=ClipboardUtils.js.map
